// ----------------------------------------------------------------------
// <copyright file="Izenda.DataSourceTree.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { invoke, get, isNil, isEmpty, map, noop, filter } from 'lodash/fp';
import classnames from 'classnames';
import draggable from 'share/components/hoc/Izenda.Draggable';
import connect from 'share/enhancers/Izenda.Connect';
import TreeView from 'share/components/controls/Izenda.TreeView';
import DataCategoryItem from './Izenda.DataCategoryItem';
import DataObjectItem from './Izenda.DataObjectItem';
import DataFieldItem from './Izenda.DataFieldItem';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import FieldPropertiesSchema from './Izenda.FieldPropertiesSchema';
import CalculatedFieldPropertiesSchema from './Izenda.CalculatedFieldPropertiesSchema';

import actions from './Izenda.DataSourceTreeActions';

const CALCULATED_FIELD_CATE_NAME = 'Calculated Fields';

/**
 * DataTree component
 * Example :
 * <DataTree store={this.props.store} />
 */
@connect(actions)
export default class DataTree extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    expandFieldLevel: PropTypes.bool,
    useCheckBox: PropTypes.bool
  };

  static defaultProps = {
    expandFieldLevel: false,
    useCheckBox: true
  };

  shouldComponentUpdate() {
    return false;
  }

  render() {
    const {props} = this;
    const classes = classnames('reports-dtsourcetree', props.className);
    const nodes = this.buildTree();
    const getChildNodes = invoke('getChildNodes');

    return (
      <div className={classes}>
        <TreeView
          className='u_paddingRight'
          nodes={nodes}
          renderNode={this.renderNode.bind(this)}
          getKey={this.getKey}
          getChildNodes={getChildNodes}
          nodeExpand={this.shouldExpand}
        />
      </div>
    );
  }

  renderNode(node, toggleIcon, toggle, isCollapsed) {
    switch (node.nodeType) {
      case 'Category': {
        return this.renderCategory(node.source, toggleIcon, toggle, isCollapsed);
      }
      case 'QuerySource': {
        return this.renderQuerySource(node.source, toggleIcon, toggle, isCollapsed);
      }
      case 'Field': {
        return this.renderField(node.source, node.querySource, toggleIcon, toggle, isCollapsed);
      }
      default: {
        return null;
      }
    }
  }

  renderCategory(node, toggleIcon, toggle, isCollapsed) {
    return (
      <DataCategoryItem dataSource={node}
                        collapseToggle={toggle}
                        isCollapsed={isCollapsed}
      />
    );
  }

  renderQuerySource(node, toggleIcon, toggle, isCollapsed) {
    const {store, toggleSourceSelection, useCheckBox} = this.props;
    const {dataTree: {filterText}} = store;

    return (
      <DataObjectItem
        dataSource={node}
        filterText={filterText}
        store={store}
        useCheckBox={useCheckBox}
        handleCheck={toggleSourceSelection}
        toggleIcon={toggleIcon}
        collapseToggle={toggle}
        isCollapsed={isCollapsed}
      />
    );
  }

  renderField(node, querySource, toggleIcon, toggle, isCollapsed) {
    const {store, deleteCalculatedField, uiStore} = this.props;
    const isCalculatedField = querySource.name === CALCULATED_FIELD_CATE_NAME && node.isCalculated;

    const buildFieldItem = function () {
      return (
        <DataFieldItem
          itemData={node}
          filterText={store.dataTree.filterText}
          store={store}
          isCalculated={isCalculatedField}
          clickDelete={deleteCalculatedField}
        />
      );
    };

    const buildDraggableFieldItem = function (uiStore) {
      const DraggableFieldItem = draggable(constants.GENNERAL.FIELD_ITEM, uiStore)(DataFieldItem);
      return (
        <DraggableFieldItem
          itemData={node}
          filterText={store.dataTree.filterText}
          dataObject={querySource}
          itemDataKey='id'
          propsEditors={getDefinedSchemaByFieldType(node)}
          isRerenderOnActive={true}
          checkRegistedItem={checkRegistedItem}
          store={store}
          isCalculated={isCalculatedField}
          clickDelete={deleteCalculatedField}
        />
      );
    };

    if (uiStore) {
      return buildDraggableFieldItem(uiStore);
    }
    return buildFieldItem();

    function checkRegistedItem(registedInfo) {
      return get('props.itemData', registedInfo) === node;
    }

    function getDefinedSchemaByFieldType(item) {
      const definedSchema = item.isCalculated && !isNil(item.reportId)
        ? CalculatedFieldPropertiesSchema
        : FieldPropertiesSchema;
      return [{
        name: 'dataSourceFieldProps',
        getDefinedSchema: definedSchema
      }];
    }
  }

  buildTree() {
    const { store } = this.props;
    const uncategorized = store.dataTree.filterSource.length === 1 && !store.dataTree.filterSource[0].name;

    const extendField = querySource => {
      return field => ({
        nodeType: 'Field',
        source: field,
        querySource: querySource,
        getChildNodes: noop
      });
    };

    const extendCate = cate => ({
      nodeType: 'Category',
      source: cate,
      getChildNodes: function () {
        if (cate.name === CALCULATED_FIELD_CATE_NAME) {
          isEmpty(cate.filterQuerySource) && (cate.filterQuerySource = []);
          return map(extendField(cate.filterQuerySource[0]), get('filterFields', cate.filterQuerySource[0]));
        }
        return map(extendQuerySource, cate.filterQuerySource);
      }
    });

    const extendQuerySource = querySource => ({
      nodeType: 'QuerySource',
      source: querySource,
      getChildNodes: function () {
        return map(
          extendField(querySource),
          filter({inaccessible: false, visible: true}, querySource.filterFields)
        );
      }
    });

    const tree = map(extendCate, store.dataTree.filterSource);
    return uncategorized ? tree[0].getChildNodes() : tree;
  }

  getKey = node => {
    // It must append name because there is possible of self-reference that has
    // same id but different name.
    const suffix = node.nodeType === 'Field' ? '_' + node.querySource.name : '';
    return node.source.id + '_' + node.source.name + suffix;
  };

  shouldExpand = node => {
    const {expandFieldLevel, store} = this.props;
    const hasFilterText = store.dataTree.filterText.length > 0;
    return node.nodeType === 'Category' || hasFilterText || (expandFieldLevel && node.nodeType === 'QuerySource');
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSourceTree/Izenda.DataSourceTree.js